import React, { useEffect, useRef, useState, useCallback } from 'react';
import { presenceHeartbeat, presenceAfmelden } from '../api/api';

const HEARTBEAT_MS = 15000;

function nieuwSessieId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Houdt aanwezigheid bij op een entiteit en geeft de andere aanwezigen terug.
 *
 * Pingt elke 15s (en direct bij aanvang) met de actuele editing-state, en
 * geeft de lijst {naam, editing} van anderen terug. De heartbeat loopt door
 * zolang de pagina gemount is — ook op een achtergrond-tabblad — zodat je
 * voor anderen zichtbaar blijft (incl. je bewerkstate). Meldt zich
 * best-effort af bij unmount.
 *
 * Robuustheid:
 * - Elke mount krijgt een EIGEN sessie_id (useRef), zodat de cleanup van een
 *   vorige mount nooit de registratie van een nieuwe mount kan afmelden.
 * - `eigenNaam` (de ingelogde gebruiker) wordt als vangnet uit de lijst
 *   gefilterd, voor het geval een na-ijlende eigen sessie nog even meekomt.
 *
 * @param {string} type   "put" | "inrichting" | "houder"
 * @param {string} id     entiteit-id (mag falsy zijn -> hook doet niets)
 * @param {boolean} editing  zit de gebruiker in bewerkmodus?
 * @param {string} eigenNaam  naam van de ingelogde gebruiker (voor het vangnet)
 * @returns {Array<{naam: string, editing: boolean}>}
 */
export function usePresence(type, id, editing, eigenNaam) {
  const [anderen, setAnderen] = useState([]);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const eigenNaamRef = useRef(eigenNaam);
  eigenNaamRef.current = eigenNaam;
  // Eén sessie-id per mount-cyclus.
  const sessieIdRef = useRef(null);
  if (sessieIdRef.current === null) sessieIdRef.current = nieuwSessieId();

  const ping = useCallback(async () => {
    if (!id) return;
    try {
      const res = await presenceHeartbeat(type, id, sessieIdRef.current, editingRef.current);
      let lijst = res.data?.anderen || [];
      // Vangnet: filter mezelf eruit (na-ijlende eigen sessie met ander id).
      const ik = eigenNaamRef.current;
      if (ik) lijst = lijst.filter((a) => a.naam !== ik);
      setAnderen(lijst);
    } catch (e) {
      // Stil falen: presence is comfort, geen kernfunctie.
    }
  }, [type, id]);

  useEffect(() => {
    if (!id) return undefined;
    // Verse sessie-id voor deze mount (vorige kan net afgemeld zijn).
    sessieIdRef.current = nieuwSessieId();
    const mijnSessie = sessieIdRef.current;

    // De heartbeat loopt ALTIJD door zolang de pagina gemount is — ook als dit
    // tabblad op de achtergrond staat. Presence gaat er immers om dat ANDEREN
    // zien dat jij de pagina open hebt (en of je aan het bewerken bent); dat
    // moet niet wegvallen zodra jij naar een ander tabblad klikt.
    ping();
    const timer = setInterval(ping, HEARTBEAT_MS);

    return () => {
      clearInterval(timer);
      // Net afmelden voor exact deze mount-sessie. Bij het hard sluiten van de
      // tab lukt dit mogelijk niet meer — dat vangt het server-side verval op.
      presenceAfmelden(type, id, mijnSessie, false).catch(() => {});
    };
  }, [type, id, ping]);

  // Bij elke wisseling van de bewerkstatus (aan én uit) meteen één keer pingen,
  // zodat collega's de wijziging binnen ~1s zien i.p.v. pas bij de volgende
  // reguliere heartbeat (max 15s later). De eerste render slaan we over: dan
  // heeft de heartbeat-start hierboven al gepingd.
  const editingEerste = useRef(true);
  useEffect(() => {
    if (editingEerste.current) { editingEerste.current = false; return; }
    if (id) ping();
  }, [editing, id, ping]);

  return anderen;
}

/**
 * Toont een compact label met de aanwezige collega's. Rendert niets als er
 * niemand anders is.
 */
export function PresenceLabel({ anderen }) {
  if (!anderen || anderen.length === 0) return null;

  const tekst = anderen
    .map((a) => (a.editing ? `${a.naam} (bewerkt)` : a.naam))
    .join(', ');
  const iemandBewerkt = anderen.some((a) => a.editing);

  return (
    <span
      className={`presence-label${iemandBewerkt ? ' presence-bewerkt' : ''}`}
      title={`Ook op deze pagina: ${tekst}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
      {tekst}
    </span>
  );
}
